import React, { memo, useEffect, useState } from 'react';
import Cookies from 'js-cookie';

import AdaptiveAnchor from '../../../AdaptiveAnchor';
import HeaderNavigationClickstreamEvents from '../../../../models/HeaderNavigationClickstreamEvents';

import {
  StyledAdaptiveAnchorContainer,
  StyledLeadingIconContainer,
  StyledLineHeight,
  StyledNavigationItem,
  StyledNewTag,
  StyledTitleText,
  StyledTrailingIconContainer,
} from './style';

import type { LinkProps } from '../../../../interfaces/linkProps';

type Props = {
  leadingIcon?: string;
  trailingIcon?: string;

  target?: string;
  title?: string | React.ReactNode;
  path?: string | null;
  escape?: boolean;
  isActive?: boolean;
  isMobileWeb?: boolean;
  // TS2709: Cannot use namespace 'StrictInlineMediaQueryProp' as a type.
  // marginBottom?: StrictInlineMediaQueryProp<keyof T_SHIRT_SIZES | number>;
  marginBottom?: string | number;
  useAbsolutePaths?: boolean;
  newTagCookieName?: string;
  showNewTag?: boolean;
  // Link component from react-router or next.js
  link?: React.ComponentType<React.PropsWithChildren<LinkProps>>;

  // TODO: find out why TS2693: 'IconType' only refers to a type, but is being used as a value here.
  // ActiveIcon?: React.ComponentType<React.PropsWithChildren<React.PropsWithChildren<typeof IconType>>> | null;
  // InactiveIcon?: React.ComponentType<React.PropsWithChildren<React.PropsWithChildren<typeof IconType>>> | null;

  ActiveIcon?: React.ReactNode | null;
  InactiveIcon?: React.ReactNode | null;
  TrailingIcon?: React.ReactNode | null;

  // onClick?: ((event?: React.MouseEvent<HTMLElement>) => void) | null;
  onClick?: (event?: React.MouseEvent<HTMLElement, MouseEvent> | undefined) => void | null;
};

const NAVIGATION_ITEM_STYLE_OVERRIDE = {
  ACTIVE_BG_COLOR_TOKEN: 'colors.backgroundWhite',
  ACTIVE_COLOR_TOKEN: 'colors.textDark',
  ACTIVE_FONT_TYPE_TOKEN: 'body',
};

// refactor code block
const NavigationItem = ({
  title,
  target = '_self',
  path = null,
  escape = false,
  isActive = false,
  isMobileWeb = false,
  useAbsolutePaths,
  link,
  marginBottom = 'xs',
  newTagCookieName,
  showNewTag = true,
  ActiveIcon = null,
  InactiveIcon = null,
  TrailingIcon = null,
  onClick,
}: Props) => {
  // Initialize state to false (hidden) to prevent flash for returning users
  const [shouldShowNewTag, setShouldShowNewTag] = useState(false);

  // When showNewTag is true: show tag only if cookie not set.
  // When false (e.g. canViewOnboardingFlow true): set cookie so tag won't reappear if flag later flips.
  useEffect(() => {
    if (newTagCookieName) {
      if (showNewTag && Cookies.get(newTagCookieName) !== 'true') {
        setShouldShowNewTag(true);
      } else {
        if (!showNewTag) {
          Cookies.set(newTagCookieName, 'true', { expires: 365 });
        }
        setShouldShowNewTag(false);
      }
    }
  }, [showNewTag, newTagCookieName]);

  // style vars for mobile web
  const activeStyleBgColorToken =
    isMobileWeb && isActive ? NAVIGATION_ITEM_STYLE_OVERRIDE.ACTIVE_BG_COLOR_TOKEN : undefined;

  const activeStyleColorToken = isMobileWeb && isActive ? NAVIGATION_ITEM_STYLE_OVERRIDE.ACTIVE_COLOR_TOKEN : undefined;
  const activeStyleFontTypeToken =
    isMobileWeb && isActive ? NAVIGATION_ITEM_STYLE_OVERRIDE.ACTIVE_FONT_TYPE_TOKEN : undefined;

  const handleClick = (event: React.MouseEvent<HTMLElement, MouseEvent> | undefined) => {
    // Set cookie and state to hide tag after first click
    if (shouldShowNewTag && newTagCookieName) {
      Cookies.set(newTagCookieName, 'true', { expires: 365 }); // 1 year
      setShouldShowNewTag(false);
    }

    if (onClick) {
      onClick(event);
    }
  };

  const renderNavigationItem = () => {
    return (
      <StyledNavigationItem
        data-testid={isActive ? 'NavigationItem-Active' : 'NavigationItem'}
        tabIndex={0}
        isActive={isActive}
        activeStyleBgColorToken={activeStyleBgColorToken}
        activeStyleColorToken={activeStyleColorToken}
      >
        <StyledLeadingIconContainer>
          {isActive ? ActiveIcon !== null && ActiveIcon : InactiveIcon !== null && InactiveIcon}
        </StyledLeadingIconContainer>
        <StyledTitleText isActive={isActive} activeStyleFontTypeToken={activeStyleFontTypeToken}>
          {title}
        </StyledTitleText>
        {shouldShowNewTag && (
          <StyledNewTag appearance="purple" size="sm">
            New
          </StyledNewTag>
        )}
        {TrailingIcon && (
          <StyledTrailingIconContainer>
            <StyledLineHeight>{TrailingIcon}</StyledLineHeight>
          </StyledTrailingIconContainer>
        )}
      </StyledNavigationItem>
    );
  };

  // TODO: find out why marginBottom displays TS error when it is a Spacer component
  return (
    // @ts-ignore
    <StyledAdaptiveAnchorContainer marginBottom={marginBottom}>
      <AdaptiveAnchor
        data-testid="NavigationItem"
        target={target}
        title={title}
        escape={escape}
        link={link}
        path={path}
        useAbsolutePaths={useAbsolutePaths}
        onClick={handleClick}
        data-eventid={HeaderNavigationClickstreamEvents.ClickNavigationLink.data_event_id}
      >
        {renderNavigationItem()}
      </AdaptiveAnchor>
    </StyledAdaptiveAnchorContainer>
  );
};

export default memo(NavigationItem);
